import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import * as messages from './messages';
import { CurrentUser } from '../../core-types';
import {
  NameForm,
  BirthYearForm,
  GenderForm,
  PostalCodeForm,
  EmailForm,
  ConnectObosForm,
  AgreedToTermsForm,
  PaymentMethodForm
} from './Steps';
import { Wizard } from '../../components/Wizard';
import { Flex, Box, Anchor, Loading } from '../../components';
import { useOnboardingSteps, StepId, Step } from './useOnboardingSteps';
import { useOnboardingQuery } from './useOnboardingQuery';
import { Sentry, Severity, captureException } from '../../lib/sentry';
import { useCurrentUser } from '../../lib/core/useCurrentUser';
import { useSettingsQuery } from '../../lib/core/SettingsQuery';

const stepComponents: Record<
  StepId,
  (props: { currentUser: CurrentUser; required: boolean }) => JSX.Element | null
> = {
  agreedToTerms: AgreedToTermsForm,
  paymentMethod: PaymentMethodForm,
  name: NameForm,
  birthYear: BirthYearForm,
  gender: GenderForm,
  postalCode: PostalCodeForm,
  email: EmailForm,
  obos: ConnectObosForm
};

export function Onboarding({
  stepId,
  onFinished,
  setStepId,
  onStepFound
}: {
  stepId?: string;
  onFinished: () => void;
  setStepId: (stepId: string) => void;
  onStepFound?: (stepId: string) => void;
}) {
  const { currentUser, ...currentUserQueryResult } = useCurrentUser();
  const onboardingQueryResult = useOnboardingQuery();
  const onboardingStepsResult = useOnboardingSteps(
    currentUser,
    undefined,
    stepId ? [stepId] : undefined
  );
  const [steps, setSteps] = useState<Step[] | undefined>();
  const { data: settingsData } = useSettingsQuery();
  const privacyPageSlug = settingsData?.settings.privacyPageSlug;

  // make sure steps is never updated after it is first loaded.
  // it is very annoying if suddenly a step is removed after the user have filled it out
  useEffect(() => {
    if (!onboardingStepsResult.steps || steps) {
      return;
    }
    setSteps(onboardingStepsResult.steps);
  }, [steps, onboardingStepsResult.steps]);

  let stepIndex = 0;

  if (stepId) {
    stepIndex = Math.max(
      steps ? steps.findIndex(step => step.id === stepId) : -1,
      0
    );
  }

  let step = steps && steps[stepIndex];

  if (!step) {
    step = steps && steps[0];
  }

  useEffect(() => {
    if (step && onStepFound) {
      onStepFound(step.id);
    }
  }, [step]);

  const loading =
    onboardingStepsResult.loading ||
    currentUserQueryResult.loading ||
    onboardingQueryResult.loading;

  if (loading) {
    return (
      <Flex alignItems="center" justifyContent="center" minHeight="100vh">
        <Loading aria-label="Loading" />
      </Flex>
    );
  }

  if (!currentUser || !steps) {
    return null;
  }

  // If no step was found, user probably shouldn't have ended up here in the first place.
  // Log an exception, and just finish the onboarding.
  if (!step) {
    Sentry.captureMessage(
      'User got to onboarding without any steps to fill in.',
      Severity.Warning
    );
    onFinished();
    return null;
  }

  const StepComponent = stepComponents[step.id];

  return (
    <div data-testid="OnboardingWizard">
      <Wizard
        stepIndex={stepIndex}
        stepCount={steps.length}
        onStepChange={props => {
          if (props.finished) {
            onFinished();
          } else if (props.nextStepIndex !== null) {
            const nextStep = steps[props.nextStepIndex];
            if (!nextStep) {
              captureException(new Error('Next step not found'), {});
              return;
            }
            if (stepId === nextStep.id) {
              return;
            }
            setStepId(nextStep.id);
          }
        }}
      >
        <Box maxWidth={{ _: '400px', lg: '600px' }}>
          <StepComponent currentUser={currentUser} required={step.required} />
          {privacyPageSlug && (
            <Box mb="sm" textAlign="center">
              <Anchor href={`/${privacyPageSlug.current}`} target="blank" mute>
                <FormattedMessage {...messages.layout.personalInformation} />
              </Anchor>
            </Box>
          )}
        </Box>
      </Wizard>
    </div>
  );
}
